import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  MessageSquare,
  MessageCircle,
  Users,
  Radio,
  LogOut,
} from "lucide-react";
import { useChannels } from "@/hooks/use-channels";
import { cn } from "@/lib/utils";
import { useAppStore } from "@/stores/app-store";
import { useAuthStore } from "@/stores/auth-store";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: number;
}

const mainNavItems: NavItem[] = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/inbox", label: "Inbox", icon: MessageSquare },
];

const managementNavItems: NavItem[] = [
  { to: "/contacts", label: "Contatos", icon: Users },
  { to: "/channels", label: "Canais", icon: Radio },
];

function getInitials(name?: string): string {
  if (!name) return "U";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function NavItemLink({
  item,
  collapsed,
  onNavigate,
  inboxOpenCount,
}: {
  item: NavItem;
  collapsed: boolean;
  onNavigate?: () => void;
  inboxOpenCount?: number;
}) {
  const badgeCount = item.to === "/inbox" ? inboxOpenCount : item.badge;

  const link = (
    <NavLink
      to={item.to}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
          "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
          isActive && "bg-sidebar-accent text-sidebar-accent-foreground",
          collapsed && "justify-center px-2",
        )
      }
    >
      <item.icon className="h-5 w-5 shrink-0" />
      {!collapsed && (
        <>
          <span className="flex-1">{item.label}</span>
          {badgeCount !== undefined && badgeCount > 0 && (
            <Badge variant="secondary" className="h-5 min-w-5 px-1.5 text-xs">
              {badgeCount}
            </Badge>
          )}
        </>
      )}
    </NavLink>
  );

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right">
          <span>{item.label}</span>
          {badgeCount !== undefined && badgeCount > 0 && (
            <span className="ml-1.5 text-muted-foreground">({badgeCount})</span>
          )}
        </TooltipContent>
      </Tooltip>
    );
  }

  return link;
}

function InboxChannelFilter({ onNavigate }: { onNavigate?: () => void }) {
  const { channels } = useChannels();
  const selectedChannelFilter = useAppStore((s) => s.selectedChannelFilter);
  const setSelectedChannelFilter = useAppStore(
    (s) => s.setSelectedChannelFilter,
  );

  const activeChannels = channels.filter((c) => c.status === "active");

  if (activeChannels.length === 0) return null;

  const handleSelect = (id: number | null) => {
    setSelectedChannelFilter(id);
    onNavigate?.();
  };

  return (
    <div className="ml-5 mt-0.5 space-y-0.5 border-l pl-2">
      <button
        onClick={() => handleSelect(null)}
        className={cn(
          "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs transition-colors",
          "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
          selectedChannelFilter === null &&
            "bg-sidebar-accent/50 font-medium text-sidebar-accent-foreground",
        )}
      >
        <MessageSquare className="h-3.5 w-3.5 shrink-0" />
        <span>Todos</span>
      </button>
      {activeChannels.map((channel) => (
        <button
          key={channel.id}
          onClick={() => handleSelect(channel.id)}
          className={cn(
            "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs transition-colors",
            "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
            selectedChannelFilter === channel.id &&
              "bg-sidebar-accent/50 font-medium text-sidebar-accent-foreground",
          )}
        >
          <MessageCircle className="h-3.5 w-3.5 shrink-0 text-green-600" />
          <span className="truncate">{channel.name}</span>
        </button>
      ))}
    </div>
  );
}

function SidebarNav({
  collapsed,
  onNavigate,
  inboxOpenCount,
}: {
  collapsed: boolean;
  onNavigate?: () => void;
  inboxOpenCount?: number;
}) {
  const location = useLocation();
  const isInbox = location.pathname.startsWith("/inbox");
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  return (
    <TooltipProvider>
      <div className="flex h-full flex-col">
        {/* Logo */}
        <div className="flex h-14 items-center border-b px-4">
          {!collapsed && (
            <span className="text-lg font-semibold">GreatChat</span>
          )}
          {collapsed && <span className="text-lg font-semibold">GC</span>}
        </div>

        {/* Navigation */}
        <div className="flex-1 overflow-y-auto p-2">
          {/* Principal section */}
          {!collapsed && (
            <p className="mb-1 px-3 pt-2 text-xs font-medium uppercase text-muted-foreground">
              Principal
            </p>
          )}
          <nav className="space-y-1">
            {mainNavItems.map((item) => (
              <NavItemLink
                key={item.to}
                item={item}
                collapsed={collapsed}
                onNavigate={onNavigate}
                inboxOpenCount={inboxOpenCount}
              />
            ))}
            {isInbox && !collapsed && (
              <InboxChannelFilter onNavigate={onNavigate} />
            )}
          </nav>

          <Separator className="my-3" />

          {/* Gestao section */}
          {!collapsed && (
            <p className="mb-1 px-3 pt-1 text-xs font-medium uppercase text-muted-foreground">
              Gestão
            </p>
          )}
          <nav className="space-y-1">
            {managementNavItems.map((item) => (
              <NavItemLink
                key={item.to}
                item={item}
                collapsed={collapsed}
                onNavigate={onNavigate}
              />
            ))}
          </nav>
        </div>

        {/* User footer */}
        <div className="border-t p-2">
          {collapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={logout}
                  className="flex w-full items-center justify-center rounded-md p-2 hover:bg-sidebar-accent transition-colors"
                >
                  <Avatar size="sm">
                    {user?.photo && (
                      <AvatarImage src={user.photo} alt={user.name} />
                    )}
                    <AvatarFallback>{getInitials(user?.name)}</AvatarFallback>
                  </Avatar>
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <div className="space-y-1">
                  <p className="font-medium">{user?.name || "Usuário"}</p>
                  <p className="text-xs text-muted-foreground">
                    {user?.email || ""}
                  </p>
                  <p className="text-xs">Clique para sair</p>
                </div>
              </TooltipContent>
            </Tooltip>
          ) : (
            <div className="flex items-center gap-3 rounded-md px-3 py-2">
              <Avatar size="sm">
                {user?.photo && (
                  <AvatarImage src={user.photo} alt={user.name} />
                )}
                <AvatarFallback>{getInitials(user?.name)}</AvatarFallback>
              </Avatar>
              <div className="flex-1 overflow-hidden">
                <p className="truncate text-sm font-medium">
                  {user?.name || "Usuário"}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {user?.email || ""}
                </p>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={logout}
                    className="shrink-0"
                  >
                    <LogOut className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">Sair</TooltipContent>
              </Tooltip>
            </div>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}

export function Sidebar({ inboxOpenCount }: { inboxOpenCount?: number }) {
  const collapsed = useAppStore((s) => s.sidebarCollapsed);

  return (
    <aside
      className={cn(
        "hidden h-full flex-col border-r bg-sidebar text-sidebar-foreground transition-all md:flex",
        collapsed ? "w-16" : "w-56",
      )}
    >
      <SidebarNav collapsed={collapsed} inboxOpenCount={inboxOpenCount} />
    </aside>
  );
}

export function MobileSidebar({ inboxOpenCount }: { inboxOpenCount?: number }) {
  const mobileOpen = useAppStore((s) => s.mobileSidebarOpen);
  const setMobileSidebarOpen = useAppStore((s) => s.setMobileSidebarOpen);
  const location = useLocation();

  const handleNavigate = () => {
    setMobileSidebarOpen(false);
  };

  return (
    <Sheet
      open={mobileOpen}
      onOpenChange={setMobileSidebarOpen}
      key={location.pathname}
    >
      <SheetContent
        side="left"
        className="w-64 p-0 bg-sidebar text-sidebar-foreground"
      >
        <SheetTitle className="sr-only">Menu de navegação</SheetTitle>
        <SidebarNav
          collapsed={false}
          onNavigate={handleNavigate}
          inboxOpenCount={inboxOpenCount}
        />
      </SheetContent>
    </Sheet>
  );
}
